import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OfferCalculationItems from "./calculation-items";
import OfferCalculationStructureTree from "./calculation-structure-tree";
import OfferCalculationContextPanel from "./calculation-context-panel";
import OfferCalculationStructureGroupDialog from "./calculation-structure-group-dialog";
import useOfferGroups from "../hooks/useOfferGroups";
import { lineAmount } from "../utils/offer-workspace";

/**
 * Three-region line-calculation workspace: structure | editor | context.
 * When `offer` is provided, left tree edits Offer::Group hierarchy (Claude numbers).
 * Reuses OfferCalculationItems; does not replace module builder.
 */
export default function OfferCalculationWorkspace(props) {
  const {
    values,
    name = "items",
    calculation,
    calculationV2Enabled = false,
    authoritativeCalculation = null,
    isLoading = false,
    offer = null,
    /** Explicit groups owner for firm templates (offer_id nil). */
    groupsOwner = null,
    onPlacementDirty = null,
    onItemsPatched = null,
    placementEpoch = 0,
    serverAdoptEpoch = 0,
    onUseTemplate = null,
    onLoadPageTemplate = null,
    onOpenPresentation = null,
    onEditNestedCalculation = null,
    onAttachNestedTemplate = null,
    onNestedModulePersist = null,
    onAddStructure = null,
    structureDialogZIndex,
    onLocalEditorChange = null,
  } = props;
  const { t } = useTranslation();

  const items = values?.[name] || [];
  const [selectedRowOrder, setSelectedRowOrder] = useState(null);
  // Grid owns live row state; Formik can lag until blur/autosave. Context must
  // follow the same source as the open accordion or props stay stale/wrong.
  const [contextItem, setContextItem] = useState(null);
  // Stable identity so autosave / Formik reinit / row_order reindex does not
  // force-close the accordion (row_order alone is renumbered by orderItems).
  const selectedRowIdentityRef = useRef(null);
  const resolvedGroupsOwner = useMemo(() => {
    if (groupsOwner?.id) return groupsOwner;
    if (offer?.id) return { ...offer, kind: "offer" };
    const calc = authoritativeCalculation || calculation || values;
    if (calc?.id && (calc.offer_id == null || calc.offer_id === undefined)) {
      return { ...calc, kind: "calculation" };
    }
    return null;
  }, [groupsOwner, offer, authoritativeCalculation, calculation, values]);

  const [structureCollapsed, setStructureCollapsed] = useState(
    !(resolvedGroupsOwner?.groups?.length)
  );
  const [contextCollapsed, setContextCollapsed] = useState(true);
  // Tree→items move bridge (items owns local row state; Formik group_id sync
  // alone does not refresh items when only offer_group_id changes).
  const [lineMoveRequest, setLineMoveRequest] = useState(null);

  const offerGroupsEnabled = Boolean(resolvedGroupsOwner?.id);
  const {
    groups,
    selectedGroupId,
    setSelectedGroupId,
    saving: groupsSaving,
    handleAddGroup,
    handleRenameGroup,
    deleteStructureGroup,
    groupDialog,
    closeGroupDialog,
    saveStructureGroup,
  } = useOfferGroups(offerGroupsEnabled ? resolvedGroupsOwner : null);

  const [itemEditorOpen, setItemEditorOpen] = useState(false);
  const localEditorOpen = itemEditorOpen || groupDialog.open || groupsSaving;
  useLayoutEffect(() => {
    onLocalEditorChange?.(localEditorOpen);
    return () => onLocalEditorChange?.(false);
  }, [localEditorOpen, onLocalEditorChange]);

  const rowIdentity = useCallback((item, index = 0) => {
    if (item?.id != null) return `id:${item.id}`;
    if (item?.draggableId) return `client:${item.draggableId}`;
    const order =
      item?.row_order !== undefined && item?.row_order !== null
        ? item.row_order
        : index;
    return `order:${order}`;
  }, []);

  // Default: no row expanded. Keep selection across soft-sync / save by
  // identity; never auto-open the first line; only clear when the row is gone.
  useEffect(() => {
    if (!items.length) {
      setSelectedRowOrder(null);
      selectedRowIdentityRef.current = null;
      return;
    }

    const identity = selectedRowIdentityRef.current;
    if (identity) {
      const idx = items.findIndex((item, index) => rowIdentity(item, index) === identity);
      if (idx < 0) {
        setSelectedRowOrder(null);
        selectedRowIdentityRef.current = null;
        return;
      }
      const order =
        items[idx].row_order !== undefined && items[idx].row_order !== null
          ? items[idx].row_order
          : idx;
      if (Number(order) !== Number(selectedRowOrder)) {
        setSelectedRowOrder(order);
      }
      return;
    }

    if (selectedRowOrder == null) return;
    const stillExists = items.some(
      (item, index) =>
        Number(
          item.row_order !== undefined && item.row_order !== null
            ? item.row_order
            : index
        ) === Number(selectedRowOrder)
    );
    if (!stillExists) {
      setSelectedRowOrder(null);
    }
  }, [items, selectedRowOrder, rowIdentity]);

  // Scroll/highlight chapter in grid when selecting a structure group.
  useEffect(() => {
    if (selectedGroupId == null) return;
    const el = document.querySelector(
      `[data-offer-calc-group="${selectedGroupId}"]`
    );
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedGroupId]);

  const handleSelectRow = useCallback(
    (rowOrder) => {
      // Accordion: clicking the open compact row collapses it; otherwise open.
      // Editor clicks must not call this (they stopPropagation) — do not
      // force-close on Formik reinit / keystroke.
      setSelectedRowOrder((prev) => {
        const closing =
          prev != null && Number(prev) === Number(rowOrder);
        const next = closing ? null : rowOrder;
        if (closing) {
          selectedRowIdentityRef.current = null;
          setContextItem(null);
        } else if (next != null) {
          const idx = items.findIndex((item, index) => {
            const order =
              item.row_order !== undefined && item.row_order !== null
                ? item.row_order
                : index;
            return Number(order) === Number(next);
          });
          selectedRowIdentityRef.current =
            idx >= 0 ? rowIdentity(items[idx], idx) : `order:${next}`;
          setContextCollapsed(false);
        }
        return next;
      });
    },
    [items, rowIdentity]
  );

  const handleSelectGroup = useCallback(
    (node) => {
      const id = node?.id ?? null;
      setSelectedGroupId(id);
      // Prefer chapter summary in context when focusing a group (no hard filter).
      setSelectedRowOrder(null);
      selectedRowIdentityRef.current = null;
      if (id != null) setContextCollapsed(false);
    },
    [setSelectedGroupId]
  );

  const handleAddStructure = useCallback(
    (parentNode = null) => {
      setStructureCollapsed(false);
      handleAddGroup(parentNode);
      onAddStructure?.(parentNode);
    },
    [handleAddGroup, onAddStructure]
  );

  const handleDeleteStructureGroup = useCallback(
    async (node) => {
      if (groupsSaving) return;
      const deletedIds = await deleteStructureGroup(node);
      if (!deletedIds?.size || typeof onItemsPatched !== "function") return;
      const nextItems = (items || []).map((row) => {
        const gid = row?.offer_group_id ?? row?.offerGroupId;
        if (gid == null || !deletedIds.has(String(gid))) return row;
        return { ...row, offer_group_id: null };
      });
      onItemsPatched(nextItems);
    },
    [deleteStructureGroup, groupsSaving, items, onItemsPatched]
  );

  const requestMoveLineToGroup = useCallback(({ rowOrder, targetGroupId }) => {
    setLineMoveRequest({
      rowOrder,
      targetGroupId: targetGroupId == null ? null : targetGroupId,
      token: Date.now(),
    });
  }, []);

  const clearLineMoveRequest = useCallback(() => {
    setLineMoveRequest(null);
  }, []);

  const selectedMeta = useMemo(() => {
    if (selectedRowOrder == null)
      return { item: null, index: null, legacyAmount: null };

    const orderOf = (item, i) =>
      item?.row_order !== undefined && item?.row_order !== null
        ? item.row_order
        : i;

    const index = items.findIndex(
      (row, i) => Number(orderOf(row, i)) === Number(selectedRowOrder)
    );

    // Prefer live grid row (contextItem) over Formik — same object the editor shows.
    const liveMatches =
      contextItem != null &&
      Number(orderOf(contextItem, index >= 0 ? index : 0)) ===
        Number(selectedRowOrder);

    if (!liveMatches && index < 0) {
      return { item: null, index: null, legacyAmount: null };
    }

    const item = liveMatches ? contextItem : items[index];
    const resolvedIndex = index >= 0 ? index : 0;
    const legacyAmount = lineAmount(item);
    return { item, index: resolvedIndex, legacyAmount };
  }, [items, selectedRowOrder, contextItem]);

  const selectedGroup = useMemo(() => {
    if (selectedGroupId == null || !offerGroupsEnabled) return null;
    return (
      groups.find((g) => String(g.id) === String(selectedGroupId)) || null
    );
  }, [groups, selectedGroupId, offerGroupsEnabled]);

  const contextSelectedItem = useMemo(() => {
    const item = selectedMeta.item;
    if (!item) return null;
    // NOTE lines have no money props — keep calc/chapter summary instead.
    if (item.line_type === "note" || item.item_type === "text") return null;
    return item;
  }, [selectedMeta.item]);

  return (
    <div
      className={[
        "offer-calc-workspace h-full min-h-0 flex-1",
        structureCollapsed ? "is-structure-collapsed" : "",
        contextCollapsed ? "is-context-collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="offer-calc-workspace__body">
        {!structureCollapsed ? (
          <div className="offer-calc-workspace__structure">
            <OfferCalculationStructureTree
              items={items}
              hierarchyGroups={offerGroupsEnabled ? groups : null}
              selectedRowOrder={selectedRowOrder}
              selectedGroupId={selectedGroupId}
              onSelectRow={handleSelectRow}
              onSelectGroup={
                offerGroupsEnabled ? handleSelectGroup : undefined
              }
              onEditGroup={offerGroupsEnabled ? handleRenameGroup : undefined}
              onDeleteGroup={
                offerGroupsEnabled ? handleDeleteStructureGroup : undefined
              }
              onAddGroup={offerGroupsEnabled ? handleAddStructure : undefined}
              onDropLineOnGroup={
                offerGroupsEnabled ? requestMoveLineToGroup : undefined
              }
              onCollapse={() => setStructureCollapsed(true)}
              calculationV2Enabled={calculationV2Enabled}
            />
          </div>
        ) : (
          <button
            type="button"
            className="offer-calc-workspace__rail offer-calc-workspace__rail--left"
            onClick={() => setStructureCollapsed(false)}
            title={t("offer.calculation.workspace.show_structure")}
            aria-label={t("offer.calculation.workspace.show_structure")}
          >
            ›
          </button>
        )}

        <div className="offer-calc-workspace__grid">
          <OfferCalculationItems
            onLocalEditorChange={setItemEditorOpen}
            {...props}
            workspaceMode
            selectedRowOrder={selectedRowOrder}
            onSelectRow={handleSelectRow}
            onContextItemChange={setContextItem}
            selectedGroupId={offerGroupsEnabled ? selectedGroupId : null}
            hierarchyGroups={offerGroupsEnabled ? groups : null}
            onSelectGroup={
              offerGroupsEnabled ? handleSelectGroup : undefined
            }
            lineMoveRequest={lineMoveRequest}
            onLineMoveApplied={clearLineMoveRequest}
            onPlacementDirty={onPlacementDirty}
            onItemsPatched={onItemsPatched}
            placementEpoch={placementEpoch}
            serverAdoptEpoch={serverAdoptEpoch}
            onUseTemplate={onUseTemplate}
            onLoadPageTemplate={onLoadPageTemplate}
            onOpenPresentation={onOpenPresentation}
            onAddStructure={
              offerGroupsEnabled ? () => handleAddStructure(null) : null
            }
            onEditNestedCalculation={onEditNestedCalculation}
            onAttachNestedTemplate={onAttachNestedTemplate}
            onNestedModulePersist={onNestedModulePersist}
            calculationV2Enabled={calculationV2Enabled}
            authoritativeCalculation={
              authoritativeCalculation || calculation
            }
            isLoading={isLoading}
          />
        </div>

        {!contextCollapsed ? (
          <div className="offer-calc-workspace__context">
            <OfferCalculationContextPanel
              calculation={authoritativeCalculation || calculation}
              selectedItem={contextSelectedItem}
              selectedIndex={contextSelectedItem ? selectedMeta.index : null}
              selectedGroup={contextSelectedItem ? null : selectedGroup}
              flatGroups={offerGroupsEnabled ? groups : []}
              allItems={items}
              calculationV2Enabled={calculationV2Enabled}
              legacyAmount={
                contextSelectedItem ? selectedMeta.legacyAmount : null
              }
              onClose={() => setContextCollapsed(true)}
            />
          </div>
        ) : null}
      </div>

      {offerGroupsEnabled ? (
        <OfferCalculationStructureGroupDialog
          open={groupDialog.open}
          mode={groupDialog.mode}
          group={groupDialog.group}
          parentGroup={groupDialog.parentGroup}
          groups={groups}
          saving={groupsSaving}
          zIndex={structureDialogZIndex}
          onClose={closeGroupDialog}
          onSave={saveStructureGroup}
        />
      ) : null}
    </div>
  );
}
